import { defineStore } from 'pinia'
import { ref, useContext } from '@nuxtjs/composition-api'
import { getIp } from '@shared/api'
import { requestAddingMenu, getMenuUser, getMenuStopList } from './../api'
import { getCartCookieRestoreState } from './../utils/cartCookie'
import {
  ICartCookie,
  ICartCookieProduct,
  IMenuUser,
  IFetchMenuUserPyload,
  IGetMenuTransform,
  IAddInCartPyload,
  IProduct,
  IModifier
} from './../model/types'

const STOP_LIST_TIMEOUT_MS = 4000

export const useMenuModel = defineStore('menu', () => {
  const { $cookies } = useContext()

  const menuUser = ref<IMenuUser[]>([])
  const activeMenu = ref(false)
  const cart = ref<any[]>([])
  const selectedModifiersMap = ref<Record<number, number[]>>({})
  const selectedSizesMap = ref<Record<number, string>>({})
  const unavailableDishIds = ref<Set<number>>(new Set())
  const unavailableModifierIds = ref<Map<string, Set<number>>>(new Map()) // cartItemId -> Set<modifierId>
  const stopListModifierIds = ref<Map<number, Set<number>>>(new Map()) // product_id -> Set<modifier_id>
  const stopListExternalModifierIds = ref<Map<string, Set<string>>>(new Map()) // external_product_id -> Set<external_modifier_id>
  const stopListGlobalExternalModifierIds = ref<Set<string>>(new Set()) // все external_modifier_id / external_product_id в стоп-листе
  const stopListExternalProductIds = ref<Set<string>>(new Set()) // external_product_id продуктов в стоп-листе

  const collectAllModifiers = (product: IProduct): IModifier[] => {
    const list: IModifier[] = []
    if (product.modifiers && product.modifiers.length > 0) {
      list.push(...product.modifiers)
    }
    if (product.modifier_groups && product.modifier_groups.length > 0) {
      product.modifier_groups.forEach((group) => {
        if (group.modifiers && group.modifiers.length > 0) {
          list.push(...group.modifiers)
        }
      })
    }
    return list
  }

  // Генерирует уникальный идентификатор элемента корзины на основе продукта, модификаторов и размера
  const generateCartItemId = (productId: number, modifiers: number[] = [], size: string | null = null): string => {
    const sortedModifiers = [...modifiers].sort((a, b) => a - b).join(',')
    const sizePart = size || ''
    return `${productId}_${sortedModifiers}_${sizePart}`
  }

  const addingMenu = async (id: number) => {
    const ip = await getIp()
    try {
      await requestAddingMenu(id, ip)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e)
    }
  }

  const setStopList = (stopListArray: any[]) => {
    if (!Array.isArray(stopListArray) || stopListArray.length === 0) {
      stopListModifierIds.value = new Map()
      stopListExternalModifierIds.value = new Map()
      stopListGlobalExternalModifierIds.value = new Set()
      stopListExternalProductIds.value = new Set()
      return
    }

    const modifierIdsMap = new Map<number, Set<number>>()
    const externalModifierIdsMap = new Map<string, Set<string>>()
    const globalExternalModifierIds = new Set<string>()
    const externalProductIdsSet = new Set<string>() // external_product_id продуктов в стоп-листе

    stopListArray.forEach((item: any) => {
      // Продукт целиком в стоп-листе
      if (item.is_stop_list && item.external_product_id) {
        externalProductIdsSet.add(String(item.external_product_id))
      }

      if (item.modifiers && Array.isArray(item.modifiers) && item.modifiers.length > 0) {
        const productId = item.product_id
        const externalProductId = item.external_product_id

        if (productId) {
          if (!modifierIdsMap.has(productId)) {
            modifierIdsMap.set(productId, new Set())
          }
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list && modifier.modifier_id) {
              modifierIdsMap.get(productId)!.add(Number(modifier.modifier_id))
            }
          })
        }

        if (externalProductId) {
          if (!externalModifierIdsMap.has(String(externalProductId))) {
            externalModifierIdsMap.set(String(externalProductId), new Set())
          }
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list) {
              if (modifier.external_modifier_id) {
                const externalModifierId = String(modifier.external_modifier_id)
                externalModifierIdsMap.get(String(externalProductId))!.add(externalModifierId)
                globalExternalModifierIds.add(externalModifierId)
              } else if (modifier.external_product_id) {
                const modifierExternalProductId = String(modifier.external_product_id)
                externalModifierIdsMap.get(String(externalProductId))!.add(modifierExternalProductId)
                globalExternalModifierIds.add(modifierExternalProductId)
              }
            }
          })
        } else {
          // external_product_id === null, складываем в глобальный список
          item.modifiers.forEach((modifier: any) => {
            if (modifier.is_stop_list) {
              if (modifier.external_modifier_id) {
                const externalModifierId = String(modifier.external_modifier_id)
                globalExternalModifierIds.add(externalModifierId)
              } else if (modifier.external_product_id) {
                const modifierExternalProductId = String(modifier.external_product_id)
                globalExternalModifierIds.add(modifierExternalProductId)
              }
            }
          })
        }
      }
    })

    stopListModifierIds.value = modifierIdsMap
    stopListExternalModifierIds.value = externalModifierIdsMap
    stopListGlobalExternalModifierIds.value = globalExternalModifierIds
    stopListExternalProductIds.value = new Set(externalProductIdsSet)
  }

  const fetchMenu = async (payload: IFetchMenuUserPyload) => {
    const { restId, restName } = payload

    // Загружаем стоп-лист: ждём максимум 4с, дальше продолжаем, но ответ всё равно применяем по завершении запроса
    const stopListPromise = getMenuStopList(restId)
      .then((responseStopList) => {
        const responseData = responseStopList?.data || responseStopList
        const stopListArray = responseData?.['stop-list'] || responseData?.stopList || []
        setStopList(stopListArray)
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.log('Stop-list request failed', e)
        setStopList([])
      })

    const stopListTimeout = new Promise<void>((resolve) => {
      setTimeout(resolve, STOP_LIST_TIMEOUT_MS)
    })

    await Promise.race([stopListPromise, stopListTimeout])

    const res: IGetMenuTransform = await getMenuUser(restId)

    activeMenu.value = res.success && res.visible === 1

    if (res.visible) {
      if (res.categories) {
        // Доваляем поле inCart
        res.categories.forEach((i) => {
          i.products.forEach((j) => {
            j.inCart = 0
          })
        })

        const cartCookie = $cookies.get('cartCookie')
        const { products: cartCookieProducts, shouldClearCookie } = getCartCookieRestoreState(cartCookie, restName)
        if (shouldClearCookie) {
          $cookies.remove('cartCookie', { path: '/' })
        }

        if (cartCookieProducts.length) {
          // Восстанавливаем корзину с правильными ценами из cookie
          const restoredCart: any[] = []
          const productMap = new Map<number, IProduct>()

          // Создаем карту продуктов для быстрого доступа
          res.categories.forEach((category) => {
            category.products.forEach((product) => {
              productMap.set(product.id, product)
            })
          })

          // Восстанавливаем все варианты продуктов из cookie
          cartCookieProducts.forEach((cookieProduct) => {
            const product = productMap.get(cookieProduct.id)
            if (product && cookieProduct.inCart && cookieProduct.inCart > 0) {
              // Восстанавливаем выбранные модификаторы из cookie
              if (cookieProduct.selectedModifiers && cookieProduct.selectedModifiers.length > 0) {
                setSelectedModifiers(product.id, cookieProduct.selectedModifiers)
              }
              // Восстанавливаем размер из cookie
              if (cookieProduct.selectedSize) {
                setSelectedSize(product.id, cookieProduct.selectedSize)
              }

              // Генерируем cartItemId если его нет в cookie (для обратной совместимости)
              const itemCartItemId = cookieProduct.cartItemId ||
                generateCartItemId(cookieProduct.id, cookieProduct.selectedModifiers || [], cookieProduct.selectedSize || null)

              // Добавляем продукт в корзину с ценой из cookie (уже включает модификаторы)
              const cartItem = {
                ...product,
                price: cookieProduct.price, // Используем цену из cookie (с модификаторами)
                our_name: cookieProduct.our_name || product.internal_name,
                selectedModifiers: cookieProduct.selectedModifiers || [],
                selectedSize: cookieProduct.selectedSize || null,
                cartItemId: itemCartItemId,
                inCart: cookieProduct.inCart
              }
              restoredCart.push(cartItem)
            }
          })

          // Обновляем inCart для продуктов в меню (суммируем все варианты)
          const productInCartMap = new Map<number, number>()
          restoredCart.forEach((item) => {
            const current = productInCartMap.get(item.id) || 0
            productInCartMap.set(item.id, current + item.inCart)
          })

          res.categories.forEach((category) => {
            category.products.forEach((product) => {
              const totalInCart = productInCartMap.get(product.id) || 0
              product.inCart = totalInCart
            })
          })

          cart.value = restoredCart
        }
        menuUser.value = res.categories
      }
    }
  }

  const addInCart = (payload: IAddInCartPyload) => {
    const { id, operator, restName, selectedModifiers, selectedSize, cartItemId } = payload

    // Находим продукт в меню
    let product: IProduct | null = null
    menuUser.value.forEach((category) => {
      const found = category.products.find(p => p.id === id)
      if (found) {
        product = found
      }
    })

    if (!product) {
      return
    }

    // Получаем модификаторы и размер из payload или из хранилища
    const modifiers = selectedModifiers || getSelectedModifiers(id)
    const size = selectedSize !== undefined ? selectedSize : getSelectedSize(id)

    // Генерируем идентификатор элемента корзины
    const itemId = cartItemId || generateCartItemId(id, modifiers, size)

    const cartCopy = [...cart.value]

    if (operator === 'plus') {
      // Ищем существующий элемент с таким же идентификатором
      const existingItemIndex = cartCopy.findIndex((item: any) => item.cartItemId === itemId)

      if (existingItemIndex > -1) {
        // Увеличиваем количество существующего элемента
        cartCopy[existingItemIndex].inCart = (cartCopy[existingItemIndex].inCart || 0) + 1
      } else {
        // Создаем новый элемент корзины
        const productCopy: any = { ...product as IProduct }
        const modifierMap = new Map<number, IModifier>()
        collectAllModifiers(product).forEach(modifier => modifierMap.set(modifier.id, modifier))

        // Рассчитываем итоговую цену с учетом модификаторов
        let totalPrice = (product as IProduct).price
        if (modifiers.length > 0) {
          modifiers.forEach((modifierId) => {
            const modifier = modifierMap.get(modifierId)
            if (modifier) {
              totalPrice += modifier.price
            }
          })
        }

        productCopy.price = totalPrice
        productCopy.selectedModifiers = [...modifiers]
        productCopy.selectedSize = size
        productCopy.cartItemId = itemId
        productCopy.inCart = 1
        productCopy.modifiers = collectAllModifiers(product)

        cartCopy.push(productCopy)
      }
    } else if (operator === 'minus') {
      // Ищем элемент для удаления
      let itemIndex = -1

      if (cartItemId) {
        // Если передан cartItemId, ищем конкретный элемент
        itemIndex = cartCopy.findIndex((item: any) => item.cartItemId === cartItemId)
      } else {
        // Для обратной совместимости: ищем первый элемент с таким productId
        itemIndex = cartCopy.findIndex((item: any) => item.id === id)
      }

      if (itemIndex > -1) {
        const item = cartCopy[itemIndex]
        if (item.inCart > 1) {
          // Уменьшаем количество
          item.inCart = item.inCart - 1
        } else {
          // Удаляем элемент из корзины
          cartCopy.splice(itemIndex, 1)
        }
      }
    }

    cart.value = cartCopy

    // Обновляем счетчики inCart в меню (суммируем все варианты одного товара)
    const productInCartMap = new Map<number, number>()
    cartCopy.forEach((item) => {
      const current = productInCartMap.get(item.id) || 0
      productInCartMap.set(item.id, current + (item.inCart || 0))
    })

    menuUser.value.forEach((category) => {
      category.products.forEach((product) => {
        const totalInCart = productInCartMap.get(product.id) || 0
        product.inCart = totalInCart
      })
    })

    // Обновляем cookie
    const objCookies: ICartCookie = {
      restaurant: restName
    }

    const inCartIDs: ICartCookieProduct[] = cartCopy.map((i: any) => {
      const item: ICartCookieProduct = {
        id: i.id,
        inCart: i.inCart,
        name: i.name,
        our_name: i.internal_name || i.our_name,
        price: i.price,
        product_picture: null,
        selectedModifiers: i.selectedModifiers && i.selectedModifiers.length > 0 ? [...i.selectedModifiers] : undefined,
        selectedSize: i.selectedSize || undefined,
        cartItemId: i.cartItemId
      }

      if (i.product_picture) {
        item.product_picture = {
          versions: {
            optimized: i.product_picture?.versions?.optimized
          }
        }
      }
      return item
    })

    objCookies.products = inCartIDs

    $cookies.set('cartCookie', objCookies, {
      maxAge: 60 * 60 * 24 * 1,
      path: '/'
    })
  }

  const setSelectedModifiers = (productId: number, modifiers: number[]) => {
    const next = { ...selectedModifiersMap.value }
    if (modifiers.length > 0) {
      next[productId] = modifiers
    } else {
      delete next[productId]
    }
    selectedModifiersMap.value = next
  }

  const getSelectedModifiers = (productId: number): number[] => {
    return selectedModifiersMap.value[productId] ? [...selectedModifiersMap.value[productId]] : []
  }

  const clearSelectedModifiers = (productId: number) => {
    const next = { ...selectedModifiersMap.value }
    if (next[productId]) {
      delete next[productId]
      selectedModifiersMap.value = next
    }
  }

  const setSelectedSize = (productId: number, sizeId: string | null) => {
    const next = { ...selectedSizesMap.value }
    if (sizeId) {
      next[productId] = sizeId
    } else {
      delete next[productId]
    }
    selectedSizesMap.value = next
  }

  const getSelectedSize = (productId: number): string | null => {
    return selectedSizesMap.value[productId] || null
  }

  const clearSelectedSize = (productId: number) => {
    const next = { ...selectedSizesMap.value }
    if (next[productId]) {
      delete next[productId]
      selectedSizesMap.value = next
    }
  }

  const updateCartForProduct = (productId: number, restName: string) => {
    // Находим продукт в menuUser
    let product: IProduct | null = null
    menuUser.value.forEach((category) => {
      const found = category.products.find(p => p.id === productId)
      if (found) {
        product = found
      }
    })

    if (!product) {
      return
    }

    const inCartCount: number = (product as IProduct).inCart || 0
    if (inCartCount <= 0) {
      return // Продукт не в корзине
    }

    // Получаем выбранные модификаторы и размер для определения конкретного варианта
    const selectedModifiers = getSelectedModifiers(productId)
    const selectedSize = getSelectedSize(productId)
    const itemCartItemId = generateCartItemId(productId, selectedModifiers, selectedSize)

    // Обновляем корзину - ищем конкретный вариант по cartItemId
    const cartCopy = [...cart.value]
    const cartItemIndex = cartCopy.findIndex((item: any) => item.cartItemId === itemCartItemId)

    if (cartItemIndex > -1) {
      // Рассчитываем итоговую цену с учетом модификаторов
      const productPrice = (product as IProduct).price
      let totalPrice = productPrice
      const modifierMap = new Map<number, IModifier>()
      collectAllModifiers(product).forEach(modifier => modifierMap.set(modifier.id, modifier))

      if (selectedModifiers.length > 0) {
        selectedModifiers.forEach((modifierId) => {
          const modifier = modifierMap.get(modifierId)
          if (modifier) {
            totalPrice += modifier.price
          }
        })
      }

      // Обновляем цену и модификаторы в корзине
      cartCopy[cartItemIndex] = {
        ...cartCopy[cartItemIndex],
        price: totalPrice,
        selectedModifiers: [...selectedModifiers],
        selectedSize: selectedSize || null
      }
      cart.value = cartCopy

      // Обновляем cookie
      const cartCookie: ICartCookie | null = $cookies.get('cartCookie')
      if (cartCookie && cartCookie.restaurant === restName && cartCookie.products) {
        const cookieProductIndex = cartCookie.products.findIndex(p => p.cartItemId === itemCartItemId)
        if (cookieProductIndex > -1) {
          cartCookie.products[cookieProductIndex] = {
            ...cartCookie.products[cookieProductIndex],
            price: totalPrice,
            selectedModifiers: selectedModifiers.length > 0 ? [...selectedModifiers] : undefined,
            selectedSize: selectedSize || undefined,
            cartItemId: itemCartItemId
          }
          $cookies.set('cartCookie', cartCookie, {
            maxAge: 60 * 60 * 24 * 1,
            path: '/'
          })
        }
      }
    }
  }

  const clearCart = () => {
    // Сбрасываем количество выбранных товаров в меню
    menuUser.value = menuUser.value.map(category => ({
      ...category,
      products: category.products.map(product => ({
        ...product,
        inCart: 0
      }))
    }))

    // Очищаем корзину, модификаторы, размеры и cookie
    cart.value = []
    selectedModifiersMap.value = {}
    selectedSizesMap.value = {}
    unavailableDishIds.value.clear()
    unavailableModifierIds.value.clear()
    $cookies.remove('cartCookie', { path: '/' })
  }

  return {
    addingMenu,
    fetchMenu,
    addInCart,
    menuUser,
    activeMenu,
    cart,
    selectedModifiersMap,
    selectedSizesMap,
    unavailableDishIds,
    unavailableModifierIds,
    stopListModifierIds,
    stopListExternalModifierIds,
    stopListGlobalExternalModifierIds,
    stopListExternalProductIds,
    setSelectedModifiers,
    getSelectedModifiers,
    clearSelectedModifiers,
    setSelectedSize,
    getSelectedSize,
    clearSelectedSize,
    updateCartForProduct,
    clearCart,
    generateCartItemId
  }
})
